import {
    Avatar,
    Button,
    Col,
    Divider,
    Image,
    Input,
    List,
    Row,
    Upload,
    message as AntMessage,
    Spin,
} from "antd";
import { Helmet } from "react-helmet-async";
import { useEffect, useRef, useState } from "react";
import { PaperClipOutlined, SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { socket } from "../../Utils/Socket";
import { API_URL, apiCall } from "../../API/RestApi";

const { TextArea, Search } = Input;

function LiveChatAdmin() {
    const token = useSelector((state) => state.user.token);
    const user = useSelector((state) => state.user.user);

    const [chats, setChats] = useState([]);
    const [chatDetail, setChatDetail] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);

    // pagination
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const chatContainerRef = useRef(null);
    const titlePage = "Live Chat Admin";

    // 🧩 Fetch daftar chat
    const fetchChats = async (keyword = "") => {
        try {
            setLoading(true);
            const res = await apiCall({
                method: "GET",
                url: `${API_URL}/v2/chat/admin?search=${keyword}`,
                data: {
                    headers: { Authorization: `Bearer ${token}` },
                },
            });
            setLoading(false);
            if (res.status === "success") {
                setChats(res.data);
            }
        } catch (err) {
            setLoading(false);
            console.error("Error fetching chats:", err);
        }
    };

    // 🧩 Fetch detail chat (pagination)
    const fetchChatDetail = async (chatId, pageNumber = 1) => {
        if (loadingDetail || !hasMore) return;
        setLoadingDetail(true);

        try {
            const res = await apiCall({
                method: "GET",
                url: `${API_URL}/v2/chat/admin/${chatId}?page=${pageNumber}&limit=20`,
                data: {
                    headers: { Authorization: `Bearer ${token}` },
                },
            });

            if (res.status === "success") {
                const newMessages = res.data || [];
                if (pageNumber === 1) {
                    setChatDetail(newMessages);
                } else if (newMessages.length > 0) {
                    setChatDetail((prev) => [...newMessages, ...prev]);
                }

                if (newMessages.length < 20) {
                    setHasMore(false);
                }
            }
        } catch (err) {
            console.error("Error fetching chat detail:", err);
        } finally {
            setLoadingDetail(false);
        }
    };

    // 🧩 Join socket room
    const joinRoom = (chatId) => {
        if (socket && chatId) {
            socket.emit("joinRoom", chatId);
        }
    };

    // 🧩 Kirim pesan
    const sendMessage = (fileEvent = null) => {
        if (!activeChat) {
            AntMessage.warning("Pilih chat user terlebih dahulu!");
            return;
        }

        const msg = text || (fileEvent ? fileEvent.file.name : "");
        if (!msg.trim()) return;

        socket.emit("sendMessage", {
            ChatId: activeChat.ChatId,
            UserId: user.id,
            Message: msg,
            Type: fileEvent ? "file" : "text",
            Url: fileEvent ? null : null,
        });

        setText("");
    };

    // 🧩 Saat klik chat user
    const handleSelectChat = (chat) => {
        setActiveChat(chat);
        setChatDetail([]);
        setPage(1);
        setHasMore(true);
        joinRoom(chat.ChatId);
        fetchChatDetail(chat.ChatId, 1);
    };

    // 🧩 Load daftar chat awal
    useEffect(() => {
        fetchChats();
    }, []);

    // 🧩 Socket listener (pesan baru realtime)
    useEffect(() => {
        socket.off("newMessage"); // hindari double listener

        socket.on("newMessage", (incoming) => {
            if (!activeChat || incoming.ChatId !== activeChat.ChatId) return;
            // kalau pesan dari admin sendiri, langsung tampil kanan
            if (incoming.UserId === null) {
                setChatDetail((prev) => [
                    ...prev,
                    {
                        ...incoming,
                        MessageSender: {
                            id: user.id,
                            RolesId: 2, // admin
                            name: user.name || "Admin",
                            email: user.email || "",
                        },
                    },
                ]);
            } else {
                // kalau pesan dari user, tampil kiri
                setChatDetail((prev) => [
                    ...prev,
                    {
                        ...incoming,
                        MessageSender: {
                            id: incoming.UserId,
                            RolesId: 3, // customer
                            name: "Customer",
                            email: "",
                        },
                    },
                ]);
            }

            // scroll ke bawah
            const container = chatContainerRef.current;
            if (container) {
                setTimeout(() => {
                    container.scrollTop = container.scrollHeight;
                }, 100);
            }
        });


        return () => socket.off("newMessage");
    }, [activeChat, user]);

    // 🧩 Scroll handler (load pesan lama)
    const handleScroll = (e) => {
        const container = e.target;
        if (container.scrollTop === 0 && hasMore && !loadingDetail) {
            const nextPage = page + 1;
            setPage(nextPage);

            const currentHeight = container.scrollHeight;
            fetchChatDetail(activeChat.ChatId, nextPage).then(() => {
                setTimeout(() => {
                    container.scrollTop = container.scrollHeight - currentHeight;
                }, 200);
            });
        }
    };

    // 🧩 Auto scroll ke bawah saat load awal
    useEffect(() => {
        if (page === 1 && chatDetail.length > 0) {
            const container = chatContainerRef.current;
            if (container) {
                setTimeout(() => {
                    container.scrollTop = container.scrollHeight;
                }, 100);
            }
        }
    }, [chatDetail, page]);

    return (
        <>
            <Helmet>
                <title>{titlePage}</title>
            </Helmet>

            <h3 className="mb-3">{titlePage}</h3>

            <Row gutter={[12, 12]}>
                {/* Sidebar Chat List */}
                <Col span={6}>
                    <Search
                        placeholder="Cari user..."
                        prefix={<SearchOutlined />}
                        allowClear
                        onSearch={(value) => fetchChats(value)}
                        style={{ marginBottom: 10 }}
                    />

                    <List
                        dataSource={chats}
                        loading={loading}
                        renderItem={(item) => (
                            <List.Item
                                onClick={() => handleSelectChat(item)}
                                style={{
                                    cursor: "pointer",
                                    background:
                                        activeChat?.ChatId === item.ChatId ? "#e6f7ff" : "white",
                                    borderRadius: 8,
                                    marginBottom: 5,
                                    padding: 10,
                                    transition: "0.2s",
                                }}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar src="https://cdn-icons-png.flaticon.com/512/847/847969.png" />
                                    }
                                    title={<strong>{item.ChatCS?.name || "Tanpa Nama"}</strong>}
                                    description={
                                        item.ChatMessages?.length > 0
                                            ? item.ChatMessages[0].Message
                                            : "Tidak ada pesan..."
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </Col>

                <Col span={0.5}>
                    <Divider type="vertical" style={{ height: "100%" }} />
                </Col>

                {/* Chat Detail Section */}
                <Col span={17}>
                    {activeChat ? (
                        <>
                            {/* Chat Area */}
                            <div
                                ref={chatContainerRef}
                                id="chat-container"
                                onScroll={handleScroll}
                                style={{
                                    overflowY: "auto",
                                    background: "#fafafa",
                                    padding: 16,
                                    height: 480,
                                    borderRadius: 10,
                                    border: "1px solid #eee",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {loadingDetail && (
                                    <div style={{ textAlign: "center", marginBottom: 10 }}>
                                        <Spin size="small" />
                                    </div>
                                )}

                                <List
                                    dataSource={chatDetail}
                                    renderItem={(item) => {
                                        const isCustomer = item.MessageSender?.RolesId === 3;
                                        const isImage = item.Type === "image" && item.Url;
                                        const isFile = item.Type === "file" && item.Url && !isImage;

                                        return (
                                            <div
                                                key={item.ChatDetailId || Math.random()}
                                                style={{
                                                    display: "flex",
                                                    flexDirection: isCustomer ? "row" : "row-reverse",
                                                    marginBottom: 10,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        backgroundColor: isCustomer ? "#f6f6ff" : "#0088ff",
                                                        color: isCustomer ? "#111" : "white",
                                                        padding: 10,
                                                        borderRadius: 12,
                                                        maxWidth: "70%",
                                                        wordBreak: "break-word",
                                                        boxShadow:
                                                            "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05)",
                                                    }}
                                                >
                                                    {isImage ? (
                                                        <Image
                                                            src={item.Url}
                                                            alt="Chat Image"
                                                            width={180}
                                                            height={180}
                                                            style={{
                                                                borderRadius: 8,
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    ) : isFile ? (
                                                        <a
                                                            href={item.Url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: isCustomer ? "#007bff" : "#fff",
                                                                textDecoration: "underline",
                                                            }}
                                                        >
                                                            📎 Download file
                                                        </a>
                                                    ) : (
                                                        item.Message
                                                    )}

                                                    <div
                                                        style={{
                                                            fontSize: 10,
                                                            color: isCustomer ? "#777" : "#ddd",
                                                            marginTop: 4,
                                                            textAlign: isCustomer ? "left" : "right",
                                                        }}
                                                    >
                                                        {moment(item.CreatedAt).format(
                                                            "D MMM YYYY, HH:mm:ss"
                                                        )}{" "}
                                                        •{" "}
                                                        <strong>{isCustomer ? "Customer" : "Admin"}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                            </div>

                            {/* Input Section */}
                            <Row style={{ marginTop: 10 }}>
                                <Col span={2}>
                                    <Upload
                                        showUploadList={false}
                                        customRequest={(op) => sendMessage(op)}
                                    >
                                        <Button
                                            shape="circle"
                                            type="default"
                                            icon={<PaperClipOutlined />}
                                        />
                                    </Upload>
                                </Col>
                                <Col span={18}>
                                    <TextArea
                                        rows={1}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="Tulis pesan ke user..."
                                        onPressEnter={() => sendMessage()}
                                        style={{
                                            borderRadius: 20,
                                            resize: "none",
                                            background: "#fff",
                                        }}
                                    />
                                </Col>
                                <Col span={4} style={{ textAlign: "right" }}>
                                    <Button
                                        type="primary"
                                        onClick={() => sendMessage()}
                                        shape="round"
                                        style={{ marginLeft: 8 }}
                                    >
                                        Kirim
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <div style={{ textAlign: "center", marginTop: 80 }}>
                            <Image
                                preview={false}
                                src="https://cdn-icons-png.flaticon.com/512/2972/2972159.png"
                                width={100}
                                height={100}
                            />
                            <h4>Halo Admin 👋</h4>
                            <p>Pilih chat user di sebelah kiri untuk melihat percakapan.</p>
                        </div>
                    )}
                </Col>
            </Row>
        </>
    );
}

export default LiveChatAdmin;
